/**
 * ResendVerificationPage
 * 
 * Pagina pubblica per richiedere un nuovo link di verifica email.
 * Accessibile da /resend-verification
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import { Mail, Loader2, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react'

export default function ResendVerificationPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    
    try {
      const response = await api.post('/onboarding/resend-verification', { email })
      
      if (response.data.success) {
        setSent(true)
      }
    } catch (err) {
      // Rate limit o altro errore
      if (err.response?.data?.code === 'RATE_LIMITED') {
        setError('Hai già richiesto un link di recente. Attendi qualche minuto prima di riprovare.')
      } else {
        // Non mostrare errori specifici per sicurezza
        // Mostra sempre successo per non rivelare se email esiste
        setSent(true)
      }
    } finally {
      setLoading(false)
    }
  }

  // Stato: email inviata con successo
  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          
          {/* Icona successo */}
          <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Controlla la tua email!
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Se <strong className="text-gray-900 dark:text-white">{email}</strong> è registrata nel nostro sistema, 
            riceverai un nuovo link di verifica entro pochi minuti.
          </p>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>💡 Suggerimento:</strong> Controlla anche la cartella spam o posta indesiderata.
            </p>
          </div>
          
          <div className="space-y-3">
            <Link 
              to="/login" 
              className="block w-full btn-primary py-3"
            >
              Torna al login
            </Link>
            
            <button
              onClick={() => {
                setSent(false)
                setEmail('')
              }}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              Invia a un'altra email
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Form per richiedere nuovo link
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Reinvia email di verifica
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400">
            Inserisci l'email usata per registrare il tuo centro. 
            Ti invieremo un nuovo link di verifica.
          </p>
        </div>

        {/* Errore */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label 
              htmlFor="email" 
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Email del centro
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="nome@esempio.it"
              autoComplete="email"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading || !email}
            className="w-full btn-primary py-3 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : (
              <>
                <Mail className="h-5 w-5 mr-2" />
                Invia link di verifica
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Link 
            to="/login" 
            className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna al login
          </Link>
        </div>

        {/* Info aggiuntive */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Problemi? Contattaci a{' '}
            <a 
              href="mailto:supporto@ildiariodibordo.it" 
              className="text-primary-600 hover:underline"
            >
              supporto@ildiariodibordo.it
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
